/**
 * Business logic for location prioritization and formatting
 */

import { sortLocationsByMarketplacePriority } from '../../../../../utils/location/sort-locations-by-marketplace-priority';
import { getLocationsForJobDetailDisplay } from './job-detail-location-parts';

/**
 * Priority keywords for location filtering
 * Locations containing these keywords should be shown first
 */
const PRIORITY_KEYWORDS = [
  // Finland
  'finland', 'suomi', 'finländ',
  // Remote variations
  'remote', 'etätyö', 'etä', 'distans', 'fjärr',
  // Global
  'global', 'maailmanlaajuinen', 'världsomfattande',
  // Remote - Europe variations
  'remote - europe', 'etätyö - eurooppa', 'etä - eurooppa', 'distans - europa', 'fjärr - europa',
  // Remote - EMEA variations
  'remote - emea', 'etätyö - emea', 'etä - emea', 'distans - emea', 'fjärr - emea',
];

/**
 * Normalizes location array from various input types
 * 
 * @param location - Location value (string, string array, or undefined)
 * @returns Array of location strings
 */
export function normalizeLocationArray(location: string | string[] | undefined): string[] {
  if (Array.isArray(location)) {
    return location.filter((loc): loc is string => typeof loc === 'string' && loc.trim() !== '');
  }
  const locStr = location as string | undefined;
  if (typeof locStr === 'string' && locStr.trim() !== '') {
    return [locStr];
  }
  return [];
}

/**
 * Prioritizes locations based on keywords
 * Returns primary locations (with priority keywords) and subtitle locations (others)
 * 
 * @param locations - Array of location strings
 * @returns Object with primaryLocations and subtitleLocations arrays
 */
export function prioritizeLocations(locations: string[]): {
  primaryLocations: string[];
  subtitleLocations: string[];
} {
  const prioritizedLocations: string[] = [];
  const otherLocations: string[] = [];
  
  locations.forEach((loc) => {
    const cleanLoc = (loc || '').replace(/,\s*Finland$/, '').trim();
    if (!cleanLoc) return;
    
    const lowerLoc = cleanLoc.toLowerCase();
    const isPriority = PRIORITY_KEYWORDS.some(keyword => lowerLoc.includes(keyword));
    
    if (isPriority) {
      prioritizedLocations.push(cleanLoc);
    } else {
      otherLocations.push(cleanLoc);
    }
  });
  
  // If we have prioritized locations, use them as primary, others as subtitle
  // Otherwise, use all locations as primary
  const primaryLocations = sortLocationsByMarketplacePriority(
    prioritizedLocations.length > 0 ? prioritizedLocations : otherLocations,
  );
  const subtitleLocations = sortLocationsByMarketplacePriority(
    prioritizedLocations.length > 0 ? otherLocations : [],
  );

  return { primaryLocations, subtitleLocations };
}

/**
 * True when the primary location line duplicates the remoteness label (e.g. both "Remote"),
 * so the UI can merge the two tiles into one compact block.
 */
export function isLocationRemotenessDisplayRedundant(
  location: string | string[] | undefined,
  remoteness: string | null | undefined,
): boolean {
  const rem = remoteness?.trim();
  if (!rem) return false;
  const { primaryLocations } = prioritizeLocations(getLocationsForJobDetailDisplay(location));
  if (primaryLocations.length === 0) return false;
  const line = primaryLocations.join(', ').trim().toLowerCase();
  return line === rem.toLowerCase();
}

